import { rawIfc } from '@viewer/lib/rawIfc'
import { extractSpatialOrigins } from '@viewer/lib/spatialPlacements'
import { fileHeightToWorldY, type DeclaredStoreyContext } from './storeys'

// ---------------------------------------------------------------------------
// Ponte entre o Elevation declarado dos pisos e o mundo three.
//
// Lê do próprio IFC (leitor web-ifc paralelo) a unidade de comprimento do
// projecto e a cota absoluta do placement a que cada piso se refere, e
// junta-lhe a matriz do modelo no mundo. Com isto o `collectStoreys`
// põe o plano de corte na cota DECLARADA, e não na base da laje.
//
// O leitor só se carrega quando os bytes do IFC estão em cache (o mesmo
// critério das unidades e do IDS). Sem ele (um .frag aberto directamente,
// por exemplo) devolve null e a cota fica calculada pela geometria,
// marcada como tal.
// ---------------------------------------------------------------------------

interface ModelLike {
  object?: {
    matrixWorld?: { elements: ArrayLike<number> }
    userData?: Record<string, unknown>
  }
}

function recentreOf(model: ModelLike): [number, number, number] {
  const t = model.object?.userData?.vn2rRecentre
  return Array.isArray(t) && t.length === 3 && t.every((v) => Number.isFinite(v))
    ? [t[0], t[1], t[2]]
    : [0, 0, 0]
}

function lerNumero(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v && typeof v === 'object') {
    const x = (v as { value?: unknown }).value
    if (typeof x === 'number' && Number.isFinite(x)) return x
  }
  return null
}

/** Contexto para a cota declarada, ou null quando não há como a ler. */
export async function declaredStoreyContext(
  modelId: string,
  model: ModelLike,
): Promise<DeclaredStoreyContext | null> {
  const m = model.object?.matrixWorld?.elements
  if (!m || m.length < 16) return null
  try {
    if (!rawIfc.hasModel(modelId) && !rawIfc.hasCachedBytes(modelId)) return null
    const origins = await extractSpatialOrigins(modelId)
    if (!origins) return null
    // Unidade lida do IfcUnitAssignment. Sem ela não se converte nada:
    // o `extractSpatialOrigins` cai para 1, e aqui isso seria assumir
    // metros.
    const unit = rawIfc.getLengthUnit(modelId)
    if (!unit || !(unit.factorToMetres > 0)) return null
    const f = unit.factorToMetres
    const parentZByStorey = new Map<number, number>()
    const elevationMByStorey = new Map<number, number>()
    for (const it of origins.items) {
      if (it.ifcType !== 'IFCBUILDINGSTOREY') continue
      // Cota absoluta do placement pai = a do piso menos o seu
      // deslocamento relativo (sem Location, o piso está na origem do pai).
      const rel = it.relative ? it.relative[2] : 0
      parentZByStorey.set(it.expressID, it.origin[2] - rel)
      // O Elevation tal como está no ficheiro, nas unidades do projecto.
      // NÃO se usa o do motor Fragments: o importador já o converteu a
      // metros, e a conversão aqui seria aplicada duas vezes.
      const e = lerNumero(
        (rawIfc.getEntity(modelId, it.expressID) as { Elevation?: unknown } | null)
          ?.Elevation,
      )
      if (e !== null) elevationMByStorey.set(it.expressID, e * f)
    }
    const t = recentreOf(model)
    const matrix = Array.from(m)
    return {
      elevationM: (localId) => elevationMByStorey.get(localId) ?? null,
      parentZ: (localId) => parentZByStorey.get(localId) ?? null,
      fileHeightToWorldY: (z) => fileHeightToWorldY(matrix, t, z),
    }
  } catch {
    return null
  }
}
